import { ApolloCache } from '@apollo/client';
import { Storage } from '@ionic/storage';

const CACHE_STORAGE_KEY = 'apollo-cache-persist';
const PERSIST_DEBOUNCE_MS = 1000;

type MutableApolloCache = Pick<
  ApolloCache,
  'write' | 'evict' | 'modify' | 'gc'
>;

/** Restore Apollo's normalized cache and persist writes using the existing Ionic Storage key. */
export async function restoreAndPersistApolloCache(
  cache: ApolloCache,
  storage: Storage,
): Promise<void> {
  const persistedCache = await storage.get(CACHE_STORAGE_KEY);
  if (persistedCache) {
    try {
      cache.restore(
        typeof persistedCache === 'string'
          ? JSON.parse(persistedCache)
          : persistedCache,
      );
    } catch (error) {
      console.error('Could not restore the persisted Apollo cache:', error);
      await storage.remove(CACHE_STORAGE_KEY);
    }
  }

  let persistTimer: ReturnType<typeof setTimeout> | undefined;
  let pendingWrite = Promise.resolve();
  const persist = () => {
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      const serializedCache = JSON.stringify(cache.extract());
      pendingWrite = pendingWrite
        .then(() => storage.set(CACHE_STORAGE_KEY, serializedCache))
        .then(() => undefined)
        .catch((error) => {
          console.error('Could not persist the Apollo cache:', error);
        });
    }, PERSIST_DEBOUNCE_MS);
  };

  // The persistence library used before Apollo Client 4 triggers on these writes.
  const mutableCache = cache as MutableApolloCache;
  const originalWrite = cache.write.bind(cache);
  mutableCache.write = (...args: Parameters<ApolloCache['write']>) => {
    const result = originalWrite(...args);
    persist();
    return result;
  };

  const originalEvict = cache.evict.bind(cache);
  mutableCache.evict = (...args: Parameters<ApolloCache['evict']>) => {
    const result = originalEvict(...args);
    persist();
    return result;
  };

  const originalModify = cache.modify.bind(cache);
  mutableCache.modify = ((...args: Parameters<ApolloCache['modify']>) => {
    const result = originalModify(...args);
    persist();
    return result;
  }) as ApolloCache['modify'];

  const originalGc = cache.gc.bind(cache);
  mutableCache.gc = (...args: Parameters<ApolloCache['gc']>) => {
    const result = originalGc(...args);
    persist();
    return result;
  };
}
